import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";

export default function useDriverCharges(drivers = []) {
  const { baseurl, token, setIsUnauthorised } = useGlobalContext();

  const [chargeRows, setChargeRows] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);

  /* ================= GET ALL DRIVER CHARGES ================= */

  const fetchDriverCharges = useCallback(
    async (silent = false) => {
      if (!silent) setChargesLoading(true);

      try {
        const res = await axios.post(
          `${baseurl}/api/Transactions/v1.0/getDriverCharges`,
          { driverCode: "" },
          { headers: { Authorization: `Basic ${token}` } }
        );

        if (res.data.responseCode === "00") {
          const normalized = (res.data.data || []).map((g) => {
            const d = drivers.find((x) => x.driverId === g.driverCode);
            const charges = g.charges || [];

            return {
              driverCode: g.driverCode,
              name: d?.fullName || d?.username || "",
              teamName: d?.teamName || "",
              per_day:
                charges.find((c) => c.type === "per_day") || null,
              per_order:
                charges.find((c) => c.type === "per_order") || null,
            };
          });

          setChargeRows(normalized);
        } else if (res.data.responseCode === "401") {
          setIsUnauthorised(true);
        } else {
          toast.error(
            res.data.responseMessage || "Failed to fetch charges"
          );
        }
      } catch {
        toast.error("Error fetching driver charges");
      } finally {
        if (!silent) setChargesLoading(false);
      }
    },
    [baseurl, token, drivers, setIsUnauthorised]
  );

  /* ================= GET CHARGES FOR SINGLE DRIVER ================= */

  const fetchChargesForDriverApi = async (
    driverCode,
    setExisting,
    setLoading
  ) => {
    if (!driverCode) return;

    setLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/Transactions/v1.0/getDriverCharges`,
        { driverCode },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        const group = (res.data.data || [])[0];
        const charges = group?.charges || [];

        const perDay =
          charges.find((c) => c.type === "per_day") || null;
        const perOrder =
          charges.find((c) => c.type === "per_order") || null;

        setExisting({
          per_day: {
            id: perDay?.id ?? null,
            amount: perDay?.amount?.toString?.() ?? "",
          },
          per_order: {
            id: perOrder?.id ?? null,
            amount: perOrder?.amount?.toString?.() ?? "",
          },
        });
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(
          res.data.responseMessage || "Failed to fetch charges"
        );
      }
    } catch {
      toast.error("Error fetching driver charges");
    } finally {
      setLoading(false);
    }
  };

  /* ================= ADD / UPDATE DRIVER CHARGES ================= */

  const saveDriverChargeApi = async ({
    adminId,
    driverCode,
    type,
    id,
    amount,
  }) => {
    if (!amount && amount !== 0) return null; // skip empty

    const isUpdate = !!id;

    const payload = isUpdate
      ? {
          adminId,
          id: String(id),
          type,
          amount: String(amount),
        }
      : {
          adminId,
          driverCode,
          type,
          amount: String(amount),
        };

    const url = isUpdate
      ? `${baseurl}/api/Transactions/v1.0/updateDriverCharges`
      : `${baseurl}/api/Transactions/v1.0/addDriverCharges`;

    return axios.post(url, payload, {
      headers: { Authorization: `Basic ${token}` },
    });
  };

  return {
    chargeRows,
    chargesLoading,
    fetchDriverCharges,
    fetchChargesForDriverApi,
    saveDriverChargeApi,
  };
}
